import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { generatePath } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import PropTypes from 'prop-types';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import useDebouncedEffect from '@/lib/hooks/useDebouncedEffect';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import translate from '@/lib/intl/translate';
import { searchConstituents } from '@/services/constituentServices';

const filterOptions = createFilterOptions();

function ConstituentAutocomplete({
  defaultValue = null,
  freeSolo = false,
  name,
  className = '',
  rules = {},
  filter = {},
  multiple = false,
  ...props
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [constituents, setConstituents] = useState([]);
  const ownerOrgId = useOwnerOrgId();
  const { control } = useFormContext();

  useDebouncedEffect(
    () => {
      (async () => {
        setIsLoading(true);
        try {
          const result = await searchConstituents({
            ownerOrgId,
            params: { displayName: searchValue, ...filter },
          });

          setConstituents(result.data?.constituents ?? []);
        } finally {
          setIsLoading(false);
        }
      })();
    },
    [searchValue, ownerOrgId],
    200,
  );

  return (
    <Controller
      {...(defaultValue && { defaultValue })}
      rules={rules}
      control={control}
      name={name}
      render={({ field: { onBlur, onChange, value: selectedValue } }) => {
        return (
          <Autocomplete
            className={className}
            freeSolo={freeSolo}
            multiple={multiple}
            value={selectedValue}
            onChange={(_, value) => onChange(value)}
            onClose={(e, reason) => {
              if (reason === 'blur' && freeSolo && e.currentTarget.value) {
                onChange({
                  inputValue: {
                    displayName: e.currentTarget.value,
                    constituentId: null,
                  },
                  displayName: e.currentTarget.value,
                });
              }
            }}
            onInputChange={(_, value) => setSearchValue(value)}
            options={constituents}
            getOptionLabel={(constituent) => constituent.displayName ?? ''}
            isOptionEqualToValue={(option, value) =>
              option?.constituentId === value?.constituentId
            }
            loading={isLoading}
            filterOptions={(options, state) => {
              const filtered = filterOptions(options, state);
              const trimmedValue = state.inputValue.trim();
              if (
                freeSolo &&
                trimmedValue &&
                !filtered
                  .map(({ displayName }) => displayName.toLowerCase())
                  .includes(trimmedValue.toLowerCase())
              ) {
                filtered.push({
                  inputValue: {
                    displayName: trimmedValue,
                    constituentId: null,
                  },
                  displayName: trimmedValue,
                });
              }
              return filtered;
            }}
            renderOption={(optionProps, constituent) => {
              return constituent?.constituentId ? (
                <li
                  className="flex w-full items-center justify-start"
                  data-testid={`option-${constituent?.constituentId}`}
                  {...optionProps}
                  key={constituent.constituentId || ''}
                >
                  {constituent.avatar && (
                    <Avatar className="mr-3" src={constituent.avatar} />
                  )}
                  <span>{constituent.displayName}</span>
                  {constituent.id && (
                    <Tooltip
                      title={translate(
                        'ADD_RELATION_DIALOG_ACTION_OPEN_PROFILE',
                      )}
                    >
                      <IconButton
                        component="a"
                        className="ml-auto"
                        href={generatePath(AuthRoutes.CONSTITUENT_INFO, {
                          constituentId: constituent.constituentId,
                        })}
                        id={`open-profile-${constituent.id}`}
                        target="_blank"
                        color="primary"
                        aria-label={translate(
                          'ADD_RELATION_DIALOG_ACTION_OPEN_PROFILE',
                        )}
                        size="small"
                        edge="start"
                      >
                        <OpenInNewIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </li>
              ) : null;
            }}
            renderInput={(params) => (
              <TextField fullWidth onBlur={onBlur} {...props} {...params} />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  data-testid={`chip-${option.constituentId}`}
                  label={option.displayName}
                  size="small"
                  {...getTagProps({ index })}
                />
              ))
            }
          />
        );
      }}
    />
  );
}

ConstituentAutocomplete.propTypes = {
  className: PropTypes.string,
  defaultValue: PropTypes.instanceOf(Date),
  freeSolo: PropTypes.bool,
  multiple: PropTypes.bool,
  name: PropTypes.string.isRequired,
  rules: PropTypes.object,
  filter: PropTypes.object,
};

export default ConstituentAutocomplete;
